import { Injectable } from '@angular/core';
import { DeeplinkMatch } from '@awesome-cordova-plugins/deeplinks/ngx';
import { Platform } from '@ionic/angular';
import { Observable, Subject } from 'rxjs';
import { Logger, LoggingService } from 'ionic-logging-service';
import { PlatformService } from '@app/services/platform/platform.service';

/**
 * A single deeplink event surfaced to subscribers.
 *
 * `matched === true`  → a sign-in redirect (`printixapp://…?code=…`) was matched;
 *                       `match` holds the plugin payload (`$args`, `$link`, …).
 * `matched === false` → a NO_MATCH, e.g. the sign-out return (`printixapp:/` with
 *                       no code); `match` is null.
 */
export type IDeeplinkEvent =
  | { matched: true; match: DeeplinkMatch }
  | { matched: false; match: null };

// The clobber target installed by ionic-plugin-deeplinks (see plugin.xml).
interface IIonicDeeplinkPlugin {
  route(
    paths: Record<string, unknown>,
    success: (match: DeeplinkMatch) => void,
    error: (nomatch: { $link: unknown }) => void
  ): void;
}

/**
 * DeeplinkService owns the app's single, lifetime-long deeplink registration.
 *
 * Why this exists: ionic-plugin-deeplinks `route()` registers a PERSISTENT native
 * callback every time it is called and offers no way to unregister it (the
 * `@awesome-cordova-plugins` `route` wrapper is declared `observable: true` with no
 * clearFunction, and the plugin itself exposes no teardown). Each `route()` call
 * therefore leaks a native handler, so every deeplink fires the match loop — and its
 * `console.log('Match found', …)` — once per leaked handler, accumulating across
 * sign-in/out cycles.
 *
 * The fix is to call `route()` exactly once for the app lifetime and multiplex the
 * result through a Subject. Callers subscribe to {@link deeplink$} (RxJS-only, safely
 * torn down) instead of arming/re-arming the plugin themselves.
 *
 * NOTE: we use the raw `IonicDeeplink` global rather than the ngx `Deeplinks.route()`
 * Observable on purpose — the ngx wrapper maps the plugin's NO_MATCH callback to
 * `observer.error`, which terminates the stream on the first sign-out return. The raw
 * callbacks are persistent, so we bridge BOTH match and no-match into the same
 * long-lived Subject and a NO_MATCH never tears the registration down.
 */
@Injectable({
  providedIn: 'root'
})
export class DeeplinkService {
  private logger: Logger;
  private readonly deeplinkSubject = new Subject<IDeeplinkEvent>();
  private registered = false;

  constructor(
    private loggingService: LoggingService,
    private platform: Platform,
    private platformService: PlatformService
  ) {
    this.logger = this.loggingService.getLogger('[DeeplinkService]');
    const methodName = 'ctor';
    this.logger.entry(methodName);
    this.registerDeeplinkRoute();
  }

  // ############### PUBLIC API ###############

  /**
   * Emits once per native deeplink for the app lifetime. Subscribe and unsubscribe
   * freely — this is a plain RxJS stream over the single native registration, so it
   * never re-arms (and never leaks) the underlying plugin handler.
   */
  public get deeplink$(): Observable<IDeeplinkEvent> {
    return this.deeplinkSubject.asObservable();
  }

  // ############### INIT ###############

  // Registers the single, app-lifetime native deeplink callback. Guarded so it runs
  // at most once and only on Cordova (the plugin does not exist on web).
  private registerDeeplinkRoute(): void {
    const methodName = 'registerDeeplinkRoute() ';
    if (!this.platformService.isCordova()) {
      this.logger.info(methodName + 'not Cordova — deeplink registration skipped');
      return;
    }
    this.platform.ready().then(() => {
      if (this.registered) {
        return;
      }
      const plugin = (window as unknown as { IonicDeeplink?: IIonicDeeplinkPlugin }).IonicDeeplink;
      if (!plugin || typeof plugin.route !== 'function') {
        this.logger.error(methodName + 'IonicDeeplink plugin unavailable');
        return;
      }
      this.registered = true;
      // The empty path '' matches the host-less sign-in redirect; the pathData value
      // is returned to subscribers as match.$route but is unused here.
      plugin.route(
        { '': true },
        (match: DeeplinkMatch) => {
          this.logger.info(methodName + 'MATCH');
          this.deeplinkSubject.next({ matched: true, match });
        },
        () => {
          this.logger.debug(methodName + 'NO_MATCH (expected on sign-out return)');
          this.deeplinkSubject.next({ matched: false, match: null });
        }
      );
    });
  }
}
